import { Box, Stack, Typography } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import {
  DocumentCard,
  ExpandableSection,
  IDocumentFile,
  SlidingPanelContent,
} from 'core/components';
import { Loader } from 'core/elements';
import { AccessPermissions } from 'core/enums';
import { PartMediaActionType } from 'parts/enums';
import { useGetPartMedia, usePartMediaActions } from 'parts/hooks';
import { IPart, IPartMediaActionPayload, IPartMediaDocument, IPartMediaImage } from 'parts/types';
import { Color, DecoratorPosition, Direction, Level, Size } from 'theme/enums';

import { DocumentExtraInfo } from './DocumentExtraInfo';
import { ImageExtraInfo } from './ImageExtraInfo';
import { UploadFileButton } from './UploadFile';
import { documentToDocumentFile, imageToDocumentFile, isUploadedImage } from './utils';
import { VideoTile } from './VideoTile';
import { PartDetailsTab } from '../enums';
import { usePartDetailsScrollToFocus } from '../hooks';

interface IProps {
  part?: IPart | null;
  enabled?: boolean;
  fullWidth?: boolean;
}

export const Media = ({ part, enabled = true, fullWidth = true }: IProps) => {
  const { t } = useTranslation();

  const { panelScrollAnchorRef } = usePartDetailsScrollToFocus(PartDetailsTab.MEDIA);

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  const isAdmin = !!user?.admin;

  const canDeleteMedia = hasPermission(AccessPermissions.PARTS_MEDIA_DELETE_IMAGE);

  const requestPayload = useMemo(
    () => ({
      number: part?.number ?? '',
      manufacturer: part?.manufacturerUrlName ?? '',
    }),
    [part?.number, part?.manufacturerUrlName]
  );

  const isQueryEnabled =
    enabled && Boolean(requestPayload.number) && Boolean(requestPayload.manufacturer);

  const { record, isLoading } = useGetPartMedia(requestPayload, isQueryEnabled);

  const { setDefaultPartImage, setDefaultPartDatasheet, deletePartMediaFile, deletePartVideo } =
    usePartMediaActions();

  const imageCards = useMemo(
    () =>
      (record?.imagesData ?? [])
        .map((image) => ({ original: image, file: imageToDocumentFile(image) }))
        .filter(
          (entry): entry is { original: IPartMediaImage; file: IDocumentFile } => !!entry.file
        ),
    [record?.imagesData]
  );

  const documentCards = useMemo(
    () =>
      (record?.documentsData ?? [])
        .map((document) => ({ original: document, file: documentToDocumentFile(document) }))
        .filter(
          (entry): entry is { original: IPartMediaDocument; file: IDocumentFile } => !!entry.file
        ),
    [record?.documentsData]
  );

  const videos = useMemo(() => record?.videosData ?? [], [record?.videosData]);

  const totalCount = imageCards.length + documentCards.length + videos.length;

  const buildActionPayload = useCallback(
    (fileId: number, type: PartMediaActionType): IPartMediaActionPayload | null => {
      if (!part?.manufacturerId) {
        return null;
      }

      return {
        file: fileId,
        manufacturerId: part.manufacturerId,
        part: part?.number ?? '',
        type,
      };
    },
    [part?.manufacturerId, part?.number]
  );

  const handleSetDefaultImage = useCallback(
    async (fileId: number) => {
      const payload = buildActionPayload(fileId, PartMediaActionType.GALLERY);

      if (!payload) {
        return;
      }
      await setDefaultPartImage(payload);
    },
    [buildActionPayload, setDefaultPartImage]
  );

  const handleSetDefaultDatasheet = useCallback(
    async (fileId: number) => {
      const payload = buildActionPayload(fileId, PartMediaActionType.DOCUMENTATION);

      if (!payload) {
        return;
      }
      await setDefaultPartDatasheet(payload);
    },
    [buildActionPayload, setDefaultPartDatasheet]
  );

  const handleRemoveImage = useCallback(
    async (fileId: number) => {
      const payload = buildActionPayload(fileId, PartMediaActionType.GALLERY);

      if (!payload) {
        return;
      }
      await deletePartMediaFile(payload);
    },
    [buildActionPayload, deletePartMediaFile]
  );

  const handleRemoveDocument = useCallback(
    async (fileId: number) => {
      const payload = buildActionPayload(fileId, PartMediaActionType.DOCUMENTATION);

      if (!payload) {
        return;
      }
      await deletePartMediaFile(payload);
    },
    [buildActionPayload, deletePartMediaFile]
  );

  const handleRemoveVideo = useCallback(
    async (videoId: number) => {
      await deletePartVideo({ partVideo: { id: videoId } });
    },
    [deletePartVideo]
  );

  const renderImageExtraInfo = useCallback(
    (image: IPartMediaImage) => (
      <ImageExtraInfo
        image={image}
        canMakeDefault={isAdmin && isUploadedImage(image)}
        onMakeDefault={handleSetDefaultImage}
      />
    ),
    [isAdmin, handleSetDefaultImage]
  );

  const renderDocumentExtraInfo = useCallback(
    (document: IPartMediaDocument) => (
      <DocumentExtraInfo
        document={document}
        canMakeDefault={isAdmin}
        onMakeDefault={handleSetDefaultDatasheet}
      />
    ),
    [isAdmin, handleSetDefaultDatasheet]
  );

  const uploadButton =
    isAdmin && record && part?.number && part?.manufacturerId ? (
      <UploadFileButton
        partNumber={part.number}
        manufacturerId={part.manufacturerId}
        manufacturerName={part.manufacturerName ?? ''}
      />
    ) : undefined;

  return (
    <Box ref={panelScrollAnchorRef} sx={{ display: 'contents' }}>
      <SlidingPanelContent
        title={t('titleMedia')}
        fullWidth={fullWidth}
        extra={uploadButton}
        scrollableContent
      >
        {isLoading && (
          <Loader
            size={Size.SM}
            style={{
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              width: 'fit-content',
              height: 'fit-content',
            }}
          />
        )}

        {!isLoading && totalCount === 0 && (
          <Typography
            color={Color.NEUTRAL}
            level={Level.TITLE_SM}
            fontStyle="italic"
            sx={{ padding: 1 }}
          >
            {t('messageNoMedia')}
          </Typography>
        )}

        {!isLoading && totalCount > 0 && (
          <Stack sx={{ gap: 2 }}>
            {imageCards.length > 0 && (
              <ExpandableSection
                title={t('labelImages')}
                defaultExpanded
                iconPosition={DecoratorPosition.START}
              >
                <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 2, padding: 1 }}>
                  {imageCards.map(({ original, file }) => (
                    <DocumentCard
                      key={`image-${original.id}`}
                      file={file}
                      sourceLabel={t('labelImage')}
                      sourceLabelColor={Color.PRIMARY}
                      onRemove={
                        canDeleteMedia && isUploadedImage(original) && !!original.fileId
                          ? handleRemoveImage
                          : undefined
                      }
                      extraInfo={renderImageExtraInfo(original)}
                    />
                  ))}
                </Stack>
              </ExpandableSection>
            )}

            {documentCards.length > 0 && (
              <ExpandableSection
                title={t('labelDocuments')}
                defaultExpanded
                iconPosition={DecoratorPosition.START}
              >
                <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 2, padding: 1 }}>
                  {documentCards.map(({ original, file }) => (
                    <DocumentCard
                      key={`document-${original.id}`}
                      file={file}
                      sourceLabel={t('labelDocument')}
                      sourceLabelColor={Color.PRIMARY}
                      onRemove={
                        canDeleteMedia && !!original.fileId ? handleRemoveDocument : undefined
                      }
                      extraInfo={renderDocumentExtraInfo(original)}
                    />
                  ))}
                </Stack>
              </ExpandableSection>
            )}

            {videos.length > 0 && (
              <ExpandableSection
                title={t('labelVideos')}
                defaultExpanded
                iconPosition={DecoratorPosition.START}
              >
                <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 2, padding: 1 }}>
                  {videos.map((video) => (
                    <VideoTile
                      key={`video-${video.id}`}
                      video={video}
                      onRemove={isAdmin ? handleRemoveVideo : undefined}
                    />
                  ))}
                </Stack>
              </ExpandableSection>
            )}
          </Stack>
        )}
      </SlidingPanelContent>
    </Box>
  );
};
